import React from "react";

import { render, screen, waitFor } from "@testing-library/react";

import Medalhas, { agruparPorFamilia } from "./Medalhas";
import axios from "../../../config/axios";

jest.mock("../../../config/axios", () => ({
  __esModule: true,
  default: { get: jest.fn() }
}));

/* Os dados são os REAIS do backend (`gam_achievements`, semeada na migration de
   24/07): duas famílias, `streak` e `xp_total`, já ordenadas por `type` e `threshold`.
   É essa ordem que a tela precisa preservar. */
const DO_SERVIDOR = [
  {
    key: "streak_5",
    type: "streak",
    name: "Primeira Semana",
    description: "5 dias seguidos de ofensiva.",
    unlocked: true,
    unlocked_at: "2026-07-30"
  },
  {
    key: "streak_10",
    type: "streak",
    name: "Duas Semanas",
    description: "10 dias seguidos de ofensiva.",
    unlocked: false,
    unlocked_at: null
  },
  {
    key: "xp_total_100",
    type: "xp_total",
    name: "Primeiros Passos",
    description: "100 XP acumulados.",
    unlocked: true,
    unlocked_at: "2026-07-28"
  },
  {
    key: "xp_total_500",
    type: "xp_total",
    name: "Em Ritmo",
    description: "500 XP acumulados.",
    unlocked: false,
    unlocked_at: null
  }
];

let avisos;
beforeEach(() => {
  jest.clearAllMocks();
  avisos = jest.spyOn(console, "warn").mockImplementation(() => {});
});
afterEach(() => avisos.mockRestore());

const responder = (dados, status = 200) =>
  axios.get.mockResolvedValue({ status, data: dados });

describe("agruparPorFamilia", () => {
  it("agrupa por `type` PRESERVANDO a ordem do servidor", () => {
    // O backend ordena por `type` e depois por `threshold`, então a ordem da resposta é
    // a que faz sentido na tela. Reordenar aqui poria "Ano Perfeito" antes de "Primeira
    // Semana".
    const grupos = agruparPorFamilia(DO_SERVIDOR);

    expect(grupos.map(g => g.type)).toEqual(["streak", "xp_total"]);
    expect(grupos[0].medalhas.map(m => m.key)).toEqual([
      "streak_5",
      "streak_10"
    ]);
    expect(grupos[0].familia.nome).toBe("Ofensiva");
    expect(grupos[1].familia.nome).toBe("Experiência");
  });

  it("família DESCONHECIDA não desaparece — cai no grupo padrão", () => {
    // `type` é uma string livre no banco, então uma família nova pode chegar sem este
    // arquivo saber. O pior resultado possível é ela aparecer sem cor, nunca invisível.
    const grupos = agruparPorFamilia([
      {
        key: "x",
        type: "trofeu_de_turma",
        name: "Turma Campeã",
        unlocked: false
      }
    ]);

    expect(grupos).toHaveLength(1);
    expect(grupos[0].familia.nome).toBe("Outras conquistas");
  });

  it("não quebra com lista vazia nem com item nulo", () => {
    expect(agruparPorFamilia()).toEqual([]);
    expect(agruparPorFamilia([null, undefined])).toEqual([]);
  });
});

describe("Medalhas", () => {
  it("separa as duas FAMÍLIAS, cada uma com nome", async () => {
    // Era o defeito: doze cartões idênticos em fila, misturando "5 dias seguidos" com
    // "1500 XP", com o campo `type` chegando na resposta e sendo ignorado.
    responder({ success: true, data: DO_SERVIDOR });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(screen.getByText("Ofensiva")).toBeInTheDocument()
    );
    expect(screen.getByText("Experiência")).toBeInTheDocument();
    expect(document.querySelectorAll(".conquistas__familia")).toHaveLength(2);
  });

  it("a medalha BLOQUEADA mantém nome e descrição legíveis", async () => {
    // O `.achievement-locked` antigo aplicava opacity e grayscale no cartão inteiro, o
    // que apagava o texto junto — e o texto de uma medalha bloqueada é o que ela tem de
    // mais útil, porque diz o que fazer para ganhá-la.
    responder({ success: true, data: DO_SERVIDOR });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(screen.getByText("Duas Semanas")).toBeInTheDocument()
    );
    expect(
      screen.getByText("10 dias seguidos de ofensiva.")
    ).toBeInTheDocument();
    expect(
      document.querySelectorAll(".conquistas__medalha--bloqueada")
    ).toHaveLength(2);
  });

  it("mostra a data de quem conquistou e o pendente de quem não", async () => {
    responder({ success: true, data: DO_SERVIDOR });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(screen.getByText("Conquistada em 30/07/2026")).toBeInTheDocument()
    );
    // Rodapé sempre presente: sem ele o cartão fica mais baixo e a fileira serrilha.
    expect(screen.getAllByText("Ainda não conquistada")).toHaveLength(2);
  });

  it("REPORTA o resumo para o cabeçalho da seção", async () => {
    responder({ success: true, data: DO_SERVIDOR });
    const onResumo = jest.fn();
    render(<Medalhas token="tok" onResumo={onResumo} />);

    await waitFor(() =>
      expect(onResumo).toHaveBeenCalledWith({ conquistadas: 2, total: 4 })
    );
  });

  it("204 (não é aluno) é lista vazia, e não falha", async () => {
    // `AchievementController@me` devolve 204 sem corpo para quem não é aluno; sem a
    // guarda, o professor veria a seção como erro.
    axios.get.mockResolvedValue({ status: 204, data: "" });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(screen.getByText(/Nenhuma medalha disponível/)).toBeInTheDocument()
    );
    expect(avisos).not.toHaveBeenCalled();
  });

  it("erro de rede mostra recado, e não spinner eterno", async () => {
    // O `.catch(() => {})` vazio de antes deixava a página inteira em spinner para
    // sempre, sem nada no console.
    axios.get.mockRejectedValue({ response: { status: 500 } });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(
        screen.getByText(/Não foi possível carregar suas medalhas/)
      ).toBeInTheDocument()
    );
    expect(avisos).toHaveBeenCalledWith(expect.stringContaining("HTTP 500"));
  });

  it("200 com `success: false` também mostra recado", async () => {
    responder({ success: false, error: "deu erro" });
    render(<Medalhas token="tok" />);

    await waitFor(() =>
      expect(
        screen.getByText(/Não foi possível carregar suas medalhas/)
      ).toBeInTheDocument()
    );
    expect(avisos).toHaveBeenCalledWith(
      expect.stringContaining("respondeu sem sucesso"),
      "deu erro"
    );
  });
});
